import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format, differenceInCalendarDays } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { CalendarDays, Minus, Plus, Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { VILLA } from "../data";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full bg-transparent border-b border-[var(--line)] py-3 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:border-terracotta transition-colors";

export const Contact = () => {
  const { lang, t } = useLang();
  const loc = lang === "fr" ? fr : enUS;
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", message: "" });
  const [range, setRange] = useState(undefined);
  const [guests, setGuests] = useState(2);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [booked, setBooked] = useState([]);
  const [showRecap, setShowRecap] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/bookings`)
      .then((r) =>
        setBooked(
          r.data.map((b) => ({
            from: new Date(`${b.start_date}T00:00:00`),
            to: new Date(`${b.end_date}T00:00:00`),
          }))
        )
      )
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error(t.contact.errFields);
      return;
    }
    setShowRecap(true);
  };

  const doSend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        guests,
        start_date: range?.from ? format(range.from, "yyyy-MM-dd") : null,
        end_date: range?.to ? format(range.to, "yyyy-MM-dd") : null,
      });
      setShowRecap(false);
      setDone(true);
      toast.success(t.contact.ok);
      setForm({ first_name: "", last_name: "", phone: "", email: "", message: "" });
      setRange(undefined);
      setGuests(2);
    } catch (err) {
      toast.error(t.contact.errSend);
    } finally {
      setSending(false);
    }
  };

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : null;

  return (
    <section id="contact" className="bg-sand-surface border-t border-[var(--line)]" data-testid="contact-section">
      <div className="grid lg:grid-cols-2 max-w-[1600px] mx-auto">
        <div className="p-8 md:p-16 lg:p-24 flex flex-col justify-center">
          <span className="text-[11px] tracking-[0.35em] uppercase text-terracotta">{t.contact.eyebrow}</span>
          <h2 className="font-serif font-light text-4xl md:text-6xl mt-4 tracking-tight leading-[1.02]">
            {t.contact.titleA} <span className="italic">{t.contact.titleB}</span>
          </h2>
          <p className="text-ink-soft font-light mt-6 max-w-md leading-relaxed">{t.contact.body}</p>
        </div>

        <div className="p-8 pb-24 md:p-16 md:pb-28 lg:p-20 lg:pb-32 bg-sand-bg border-t lg:border-t-0 lg:border-l border-[var(--line)]">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="h-full min-h-[300px] flex flex-col items-center justify-center text-center"
              data-testid="contact-success"
            >
              <div className="w-14 h-14 rounded-full bg-olive/15 flex items-center justify-center mb-6">
                <Check className="w-6 h-6 text-olive" />
              </div>
              <h3 className="font-serif text-3xl font-light mb-3">{t.contact.successTitle}</h3>
              <p className="text-ink-soft font-light max-w-sm">{t.contact.successBody}</p>
              <button onClick={() => setDone(false)} data-testid="new-inquiry-btn" className="mt-8 text-sm link-underline text-ink">
                {t.contact.another}
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-5">
                <input data-testid="input-first-name" className={field} placeholder={t.contact.firstName} value={form.first_name} onChange={set("first_name")} />
                <input data-testid="input-last-name" className={field} placeholder={t.contact.lastName} value={form.last_name} onChange={set("last_name")} />
              </div>
              <input data-testid="input-email" type="email" className={field} placeholder={t.contact.email} value={form.email} onChange={set("email")} />
              <input data-testid="input-phone" className={field} placeholder={t.contact.phone} value={form.phone} onChange={set("phone")} />

              <div>
                <div className="grid grid-cols-2 gap-5">
                  {[
                    { key: "from", label: t.contact.arrival, testid: "arrival-trigger" },
                    { key: "to", label: t.contact.departure, testid: "departure-trigger" },
                  ].map((f) => (
                    <Popover key={f.key}>
                      <PopoverTrigger asChild>
                        <button type="button" data-testid={f.testid} className={`${field} flex items-center justify-between text-left`}>
                          <span className={range?.[f.key] ? "text-ink" : "text-ink-soft/60"}>
                            {range?.[f.key] ? format(range[f.key], "d MMM yyyy", { locale: loc }) : f.label}
                          </span>
                          <CalendarDays className="w-4 h-4 text-ink-soft shrink-0" strokeWidth={1.4} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-sand-bg border-[var(--line)]" align="start">
                        <Calendar
                          mode="range"
                          selected={range}
                          onSelect={setRange}
                          numberOfMonths={1}
                          locale={loc}
                          classNames={{
                            day_outside: "invisible pointer-events-none",
                            day_selected: "aria-selected:bg-terracotta aria-selected:text-white rounded-full font-medium",
                            day_range_start: "aria-selected:bg-terracotta aria-selected:text-white rounded-full font-medium",
                            day_range_end: "aria-selected:bg-terracotta aria-selected:text-white rounded-full font-medium",
                            day_range_middle: "aria-selected:bg-terracotta/20 aria-selected:text-ink rounded-none",
                          }}
                          modifiers={{ booked }}
                          modifiersClassNames={{ booked: "day-booked" }}
                          disabled={[{ before: new Date() }, ...booked]}
                          data-testid="date-range-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </div>
                <p className="mt-2 text-xs text-ink-soft/80 flex items-center gap-2" data-testid="booked-legend">
                  <span className="inline-block w-3 h-3 bg-[var(--line)]" />
                  {t.contact.legend}
                </p>
              </div>

              <div className="flex items-center justify-between border-b border-[var(--line)] py-3">
                <span className="text-ink-soft text-sm">{t.contact.guests}</span>
                <div className="flex items-center gap-5">
                  <button type="button" data-testid="guests-minus" onClick={() => setGuests((g) => Math.max(1, g - 1))} className="w-8 h-8 border border-[var(--line)] flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors">
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span data-testid="guests-count" className="font-serif text-2xl w-6 text-center">{guests}</span>
                  <button type="button" data-testid="guests-plus" onClick={() => setGuests((g) => Math.min(10, g + 1))} className="w-8 h-8 border border-[var(--line)] flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <textarea data-testid="input-message" rows={4} className={`${field} resize-none`} placeholder={t.contact.message} value={form.message} onChange={set("message")} />

              <button
                type="submit"
                disabled={sending}
                data-testid="submit-inquiry-btn"
                className="group w-full bg-ink text-sand-bg py-4 text-sm tracking-wide hover:bg-terracotta transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? t.contact.sending : t.contact.send}
              </button>
            </form>
          )}
        </div>
      </div>

      <Dialog open={showRecap} onOpenChange={setShowRecap}>
        <DialogContent className="bg-sand-bg border-[var(--line)] max-w-md" data-testid="recap-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif font-light text-2xl tracking-tight">{t.contact.recapTitle}</DialogTitle>
          </DialogHeader>
          <div className="divide-y divide-[var(--line)] text-sm" data-testid="recap-details">
            <div className="flex items-center justify-between py-3">
              <span className="text-ink-soft">{t.contact.arrival}</span>
              <span className="font-medium" data-testid="recap-arrival">
                {range?.from ? format(range.from, "EEEE d MMMM yyyy", { locale: loc }) : t.contact.recapNoDates}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-ink-soft">{t.contact.departure}</span>
              <span className="font-medium" data-testid="recap-departure">
                {range?.to ? format(range.to, "EEEE d MMMM yyyy", { locale: loc }) : t.contact.recapNoDates}
              </span>
            </div>
            {nights !== null && (
              <div className="flex items-center justify-between py-3">
                <span className="text-ink-soft">{t.contact.recapNights}</span>
                <span className="font-medium" data-testid="recap-nights">{nights}</span>
              </div>
            )}
            <div className="flex items-center justify-between py-3">
              <span className="text-ink-soft">{t.contact.guests}</span>
              <span className="font-medium" data-testid="recap-guests">{guests}</span>
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={() => setShowRecap(false)}
              data-testid="recap-edit-btn"
              className="flex-1 border border-ink text-ink py-3 text-sm tracking-wide hover:bg-ink hover:text-sand-bg transition-colors duration-300"
            >
              {t.contact.recapEdit}
            </button>
            <button
              type="button"
              onClick={doSend}
              disabled={sending}
              data-testid="recap-confirm-btn"
              className="flex-1 bg-ink text-sand-bg py-3 text-sm tracking-wide hover:bg-terracotta transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? t.contact.sending : t.contact.recapConfirm}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
